import resolvePullDistance, {
	PULL_REVEAL_PX,
	PULL_THRESHOLD_PX,
} from '@Guidelines/Shell/PullToRefresh/resolvePullDistance';
import * as React from 'react';

export type PullPhase = 'idle' | 'pulling' | 'armed' | 'blocked' | 'refreshing';

export type PullBlockReason = 'capture' | 'offline';

type Args = {
	readonly isEnabled: boolean;
	readonly blockReason: PullBlockReason | null;
	readonly onRefresh: () => void;
};

type PullGesture = {
	readonly phase: PullPhase;
	readonly distance: number;
	readonly blockReason: PullBlockReason | null;
};

type Tracking = {
	readonly startX: number;
	readonly startY: number;
	isDecided: boolean;
};

const IDLE_GESTURE: PullGesture = { phase: 'idle', distance: 0, blockReason: null };

const MODAL_SELECTOR = '[role="dialog"],[aria-modal="true"]';

const hasScrolledAncestor = (target: EventTarget | null): boolean => {
	let element = target instanceof Element ? target : null;

	while (element !== null) {
		if (element.scrollTop > 0) {
			return true;
		}
		element = element.parentElement;
	}

	return false;
};

const isInsideModal = (target: EventTarget | null): boolean => {
	return target instanceof Element && target.closest(MODAL_SELECTOR) !== null;
};

const isPageAtTop = (): boolean => {
	return (document.scrollingElement?.scrollTop ?? 0) <= 0;
};

/**
 * Tracks a downward drag from the top of the page on `window`. Nothing is attached while disabled.
 * The gesture stays invisible (idle, page scrolls normally) until the damped distance reaches `PULL_REVEAL_PX`.
 * `blockReason` is non-null in the returned gesture only while the phase is `blocked`.
 * `blockReason` and `onRefresh` are read through refs so a change never re-attaches the listeners.
 */
function usePullToRefreshGesture({ isEnabled, blockReason, onRefresh }: Args): PullGesture {
	const [gesture, setGesture] = React.useState<PullGesture>(IDLE_GESTURE);
	const blockReasonRef = React.useRef<PullBlockReason | null>(blockReason);
	const onRefreshRef = React.useRef<() => void>(onRefresh);

	React.useEffect(() => {
		blockReasonRef.current = blockReason;
		onRefreshRef.current = onRefresh;
	}, [blockReason, onRefresh]);

	React.useEffect(() => {
		if (!isEnabled) {
			return undefined;
		}

		let tracking: Tracking | null = null;
		let current: PullGesture = IDLE_GESTURE;

		const update = (next: PullGesture): void => {
			current = next;
			setGesture(next);
		};

		const reset = (): void => {
			tracking = null;
			if (current.phase !== 'idle') {
				update(IDLE_GESTURE);
			}
		};

		const handleTouchStart = (event: TouchEvent): void => {
			if (current.phase === 'refreshing') {
				return;
			}
			const touch = event.touches[0];
			if (event.touches.length !== 1 || touch === undefined) {
				reset();
				return;
			}
			if (!isPageAtTop() || hasScrolledAncestor(event.target) || isInsideModal(event.target)) {
				tracking = null;
				return;
			}
			tracking = { startX: touch.clientX, startY: touch.clientY, isDecided: false };
		};

		const handleTouchMove = (event: TouchEvent): void => {
			if (tracking === null) {
				return;
			}
			const touch = event.touches[0];
			if (event.touches.length !== 1 || touch === undefined) {
				reset();
				return;
			}
			const deltaY = touch.clientY - tracking.startY;
			const deltaX = touch.clientX - tracking.startX;

			if (!tracking.isDecided) {
				if (deltaY <= 0 || Math.abs(deltaX) > Math.abs(deltaY)) {
					tracking = null;
					return;
				}
				tracking.isDecided = true;
			}
			if (deltaY <= 0) {
				reset();
				return;
			}
			const distance = resolvePullDistance(deltaY);

			if (distance < PULL_REVEAL_PX) {
				if (current.phase !== 'idle') {
					update(IDLE_GESTURE);
				}
				return;
			}
			if (event.cancelable) {
				event.preventDefault();
			}

			const reason = blockReasonRef.current;
			if (reason !== null) {
				update({ phase: 'blocked', distance, blockReason: reason });
				return;
			}
			update({
				phase: distance >= PULL_THRESHOLD_PX ? 'armed' : 'pulling',
				distance,
				blockReason: null,
			});
		};

		const handleTouchEnd = (): void => {
			if (tracking === null) {
				return;
			}
			const shouldRefresh = current.phase === 'armed' && blockReasonRef.current === null;
			tracking = null;

			if (shouldRefresh) {
				update({ phase: 'refreshing', distance: PULL_THRESHOLD_PX, blockReason: null });
				onRefreshRef.current();
				return;
			}
			if (current.phase !== 'idle') {
				update(IDLE_GESTURE);
			}
		};

		window.addEventListener('touchstart', handleTouchStart, { passive: true });
		window.addEventListener('touchmove', handleTouchMove, { passive: false });
		window.addEventListener('touchend', handleTouchEnd);
		window.addEventListener('touchcancel', handleTouchEnd);

		return () => {
			window.removeEventListener('touchstart', handleTouchStart);
			window.removeEventListener('touchmove', handleTouchMove);
			window.removeEventListener('touchend', handleTouchEnd);
			window.removeEventListener('touchcancel', handleTouchEnd);
		};
	}, [isEnabled]);

	return gesture;
}

export default usePullToRefreshGesture;
